/**
 * Framework-agnostic GA4 article scroll-depth tracker.
 *
 * Sends `article_view` once per article view and one `article_scroll_depth`
 * event containing the maximum configured threshold reached (25/50/75/90 by
 * default) when the view ends. Events are gated on the page's existing GA4
 * Consent Mode signal (read from window.dataLayer -- no separate consent
 * mechanism is introduced, and no cookie/localStorage/sessionStorage is ever
 * written). Zero framework dependencies: the same module works unmodified from
 * Next.js, plain React, Astro, or a bare <script> tag.
 *
 * Usage (e.g. inside a React effect):
 *
 *   const tracker = createArticleScrollDepthTracker({
 *     containerSelector: ".wp-content",
 *     articleId: article.id,
 *     canonicalUrl: article.getArticlePageCanonLink,
 *     transport: "gtag",
 *     measurementId: "G-SNL69JD7W0",
 *     // Optional, all backward-compatible when omitted:
 *     pageType: "post",       // emitted as page_type on every event
 *     eventPrefix: "article", // renames events/params: {prefix}_view, {prefix}_id, ...
 *   });
 *   tracker.start();
 *
 *   // on client-side navigation to a different article:
 *   tracker.switchArticle({ articleId: next.id, canonicalUrl: next.getArticlePageCanonLink });
 *
 *   // on unmount:
 *   tracker.destroy();
 */

import {
  createArticleViewSession,
  isArticleViewSession,
  type ArticleViewSession,
} from "../shared/createArticleViewSession";
import { createConsentGate } from "../shared/createConsentGate";
import { pushDataLayerEvent, type DataLayerTransport } from "../shared/pushDataLayerEvent";
import { createActiveInteractionTimer } from "../shared/createActiveInteractionTimer";

const DEFAULT_THRESHOLDS = [25, 50, 75, 90];
const DEFAULT_EVENT_PREFIX = "article";

export interface ArticleScrollDepthTrackerConfig {
  articleId?: string | number;
  containerSelector?: string;
  canonicalUrl?: string;
  measurementId?: string;
  pageType?: string;
  eventPrefix?: string;
  transport?: DataLayerTransport;
  thresholds?: number[];
  defaultConsentState?: "granted" | "denied";
  debug?: boolean;
  viewSession?: ArticleViewSession;
}

interface NormalizedConfig {
  articleId?: string | number;
  containerSelector: string | null;
  canonicalUrl?: string;
  measurementId?: string;
  pageType: string | null;
  eventPrefix: string;
  transport: DataLayerTransport;
  thresholds: number[];
  defaultConsentState: "granted" | "denied";
  debug: boolean;
}

export interface ArticleScrollDepthTracker {
  start(): void;
  switchArticle(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }): void;
  destroy(): void;
  getDebugState(): Record<string, unknown> | null;
}

function sanitizeEventPrefix(raw: unknown): string {
  const prefix = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  // GA4 event names must be alphanumeric/underscore and start with a letter.
  return /^[a-z][a-z0-9_]*$/.test(prefix) ? prefix : DEFAULT_EVENT_PREFIX;
}

function warn(config: { debug: boolean }, ...args: unknown[]): void {
  if (config.debug && typeof console !== "undefined") {
    console.warn("[ArticleScrollDepthTracker]", ...args);
  }
}

function safely<T>(config: { debug: boolean }, fn: () => T): T | undefined {
  try {
    return fn();
  } catch (error) {
    warn(config, "swallowed an internal error", error);
    return undefined;
  }
}

export function stripQueryAndFragment(url?: string | null): string | undefined | null {
  if (!url) return url;
  try {
    const base = typeof location !== "undefined" ? location.href : undefined;
    const parsed = new URL(url, base);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (error) {
    return String(url).split("?")[0].split("#")[0];
  }
}

// Resolves the container element from a CSS selector at the moment it's
// needed, rather than requiring the caller to have looked it up already.
// document.querySelector() itself already returns only the first match, so
// a selector matching multiple elements (e.g. a shared class name) can never
// resolve to more than one container.
function resolveContainer(selector?: string | null): Element | null {
  if (typeof document === "undefined" || !selector) return null;
  return document.querySelector(selector);
}

function deriveCanonicalUrl(configuredUrl?: string | null): string {
  if (configuredUrl) return stripQueryAndFragment(configuredUrl) as string;
  if (typeof location === "undefined") return "";
  return stripQueryAndFragment(`${location.origin}${location.pathname}`) as string;
}

// Mirrors the bounding-rect formula already proven in production by
// src/hooks/useScrollPercentage.js's calculateVerticalPercentage (normalizes by
// total scroll-travel distance, so short articles still reach 90%+ correctly),
// reimplemented without its react-intersection-observer dependency.
export function calculateVerticalPercentage(
  bounds: { height: number; bottom: number },
  viewportHeight: number
): number {
  // A container with no height has not been laid out yet (content still
  // rendering, images not yet sized) or is display:none. Its rect reads as
  // all zeros, which the formula below would score as 100% -- indistinguishable
  // from "fully scrolled past" -- instantly marking every threshold reached on
  // an article the visitor never scrolled. Treat it as unmeasurable instead.
  // The comparisons are written as !(x > 0) so NaN is rejected too.
  if (!(bounds.height > 0) || !(viewportHeight > 0)) return 0;
  const travel = viewportHeight + bounds.height;
  if (travel <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, bounds.bottom / travel));
  return Math.round((1 - ratio) * 100);
}

function normalizeConfig(
  userConfig?: ArticleScrollDepthTrackerConfig
): NormalizedConfig {
  const raw = userConfig || {};
  const transportExplicit = raw.transport === "gtag" || raw.transport === "gtm";
  const config: NormalizedConfig = {
    articleId: raw.articleId,
    containerSelector:
      typeof raw.containerSelector === "string" ? raw.containerSelector : null,
    canonicalUrl: raw.canonicalUrl,
    measurementId: raw.measurementId,
    // Optional labelling param. Emitted only when configured, so consumers
    // that omit it keep sending byte-identical events.
    pageType: raw.pageType || null,
    eventPrefix: sanitizeEventPrefix(raw.eventPrefix),
    transport: raw.transport === "gtm" ? "gtm" : "gtag",
    thresholds:
      Array.isArray(raw.thresholds) && raw.thresholds.length
        ? raw.thresholds.slice().sort((a, b) => a - b)
        : DEFAULT_THRESHOLDS.slice(),
    defaultConsentState:
      raw.defaultConsentState === "denied" ? "denied" : "granted",
    debug: !!raw.debug,
  };

  if (!transportExplicit) {
    warn(
      config,
      'transport was not set explicitly; defaulting to "gtag". Confirm this against the ' +
        "site's GTM container configuration before relying on it in production."
    );
  }
  if (!config.containerSelector) {
    warn(
      config,
      "no containerSelector provided; scroll depth will never be measured."
    );
  }
  if (!config.articleId) {
    warn(config, "no articleId provided.");
  }
  const requestedPrefix =
    typeof raw.eventPrefix === "string" ? raw.eventPrefix.trim().toLowerCase() : "";
  if (raw.eventPrefix && config.eventPrefix !== requestedPrefix) {
    warn(
      config,
      "eventPrefix must be alphanumeric/underscore and start with a letter; " +
        'falling back to "' + DEFAULT_EVENT_PREFIX + '".',
      raw.eventPrefix
    );
  }

  return config;
}

export function createArticleScrollDepthTracker(
  userConfig: ArticleScrollDepthTrackerConfig
): ArticleScrollDepthTracker {
  const config = normalizeConfig(userConfig);

  let container: Element | null = null;
  let articleId = config.articleId;
  let currentCanonicalUrl: string | undefined;

  let reachedThresholds = new Set<number>();
  let viewEventSent = false;
  let maximumDepthEventSent = false;

  let started = false;
  let destroyed = false;

  let intersectionObserver: IntersectionObserver | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let isListening = false;
  let isLifecycleListening = false;
  let rafHandle: number | ReturnType<typeof setTimeout> | null = null;

  const viewSession: ArticleViewSession = isArticleViewSession(
    userConfig && userConfig.viewSession
  )
    ? (userConfig.viewSession as ArticleViewSession)
    : createArticleViewSession();
  if (userConfig && userConfig.viewSession && !isArticleViewSession(userConfig.viewSession)) {
    warn(
      config,
      "viewSession must implement { ensureViewId, getViewId, reset }; ignoring and creating an owned session."
    );
  }

  const activeTimer = createActiveInteractionTimer();

  const gate = createConsentGate({
    defaultConsentState: config.defaultConsentState,
    onGrantedChange(after) {
      activeTimer.setConsentGranted(after);
      if (after) {
        beginTrackingIfNeeded();
      } else {
        teardownIntersectionGate();
      }
    },
  });

  function sendEvent(eventName: string, extraParams: Record<string, unknown>) {
    if (destroyed) return;
    const dataLayer = gate.getDataLayer();
    if (!dataLayer) {
      warn(config, "no dataLayer available; dropping event", eventName);
      return;
    }
    const p = config.eventPrefix;
    const fields = {
      [p + "_view_id"]: viewSession.getViewId(),
      [p + "_id"]: articleId,
      [p + "_canonical_url"]: currentCanonicalUrl,
      ...(config.pageType ? { page_type: config.pageType } : {}),
      ...(config.containerSelector
        ? { scroll_container: config.containerSelector }
        : {}),
      ...extraParams,
    };
    pushDataLayerEvent({
      dataLayer,
      transport: config.transport,
      measurementId: config.measurementId,
      eventName,
      fields,
    });
  }

  function recomputeReachedThresholds() {
    if (destroyed || !container || !gate.isGranted()) return;
    const bounds = container.getBoundingClientRect();
    // Nothing to measure until the container actually has layout. Bail out
    // rather than recording a 0, so a consumer-configured 0 threshold isn't
    // marked "reached" by a pass that ran before the article rendered. A
    // later scroll/resize/ResizeObserver pass re-measures once it has height.
    if (!(bounds.height > 0)) return;
    const viewportHeight =
      (typeof window !== "undefined" && window.innerHeight) || 0;
    const percent = calculateVerticalPercentage(bounds, viewportHeight);
    config.thresholds
      .filter((threshold) => threshold <= percent)
      .forEach((threshold) => reachedThresholds.add(threshold));
  }

  function flushMaximumScrollDepth() {
    if (destroyed || maximumDepthEventSent || !gate.isGranted() || !viewEventSent)
      return;
    recomputeReachedThresholds();
    const maximumThreshold = Array.from(reachedThresholds).sort(
      (a, b) => b - a
    )[0];
    // A view that never reached the lowest threshold (a bounce) still gets
    // its one terminal event, reporting 0 -- otherwise its active_time_msec
    // would never be recorded at all, biasing every average toward the more
    // engaged views that did cross a threshold.
    maximumDepthEventSent = true;
    sendEvent(config.eventPrefix + "_scroll_depth", {
      percent_scrolled: maximumThreshold === undefined ? 0 : maximumThreshold,
      // gtag honors this as an instruction to use sendBeacon during pagehide;
      // GTM consumers can forward it with their existing event settings.
      transport_type: "beacon",
      active_time_msec: Math.round(activeTimer.getElapsedMsec()),
      active_idle_timeout_msec: activeTimer.getIdleTimeoutMsec(),
      active_time_method: "momently_v1",
    });
  }

  function scheduleRecompute() {
    if (destroyed || rafHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (fn: () => void) => setTimeout(fn, 16);
    rafHandle = schedule(() => {
      rafHandle = null;
      safely(config, () => {
        if (destroyed) return;
        // Self-heal: re-wrap dataLayer.push if something replaced the array or
        // its push method out from under us since the last frame.
        gate.reobserve();
        recomputeReachedThresholds();
      });
    });
  }

  function onPageHide() {
    safely(config, flushMaximumScrollDepth);
  }

  function attachLifecycleListener() {
    if (isLifecycleListening || destroyed || typeof window === "undefined") return;
    isLifecycleListening = true;
    window.addEventListener("pagehide", onPageHide);
  }

  function detachLifecycleListener() {
    if (!isLifecycleListening || typeof window === "undefined") return;
    isLifecycleListening = false;
    window.removeEventListener("pagehide", onPageHide);
  }

  function attachListeners() {
    if (isListening || destroyed || typeof window === "undefined") return;
    isListening = true;
    window.addEventListener("scroll", scheduleRecompute, { passive: true });
    window.addEventListener("resize", scheduleRecompute);
    if (typeof ResizeObserver === "function" && container) {
      resizeObserver = new ResizeObserver(scheduleRecompute);
      resizeObserver.observe(container);
    }
  }

  function detachListeners() {
    if (!isListening) return;
    isListening = false;
    window.removeEventListener("scroll", scheduleRecompute);
    window.removeEventListener("resize", scheduleRecompute);
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  }

  function setupIntersectionGate() {
    teardownIntersectionGate();
    if (!container) return;
    if (typeof IntersectionObserver !== "function") {
      // No IntersectionObserver support: fall back to listening unconditionally.
      attachListeners();
      return;
    }
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        safely(config, () => {
          const entry = entries[entries.length - 1];
          if (entry && entry.isIntersecting) {
            attachListeners();
            scheduleRecompute();
          } else {
            detachListeners();
          }
        });
      },
      { rootMargin: "100% 0px", threshold: 0 }
    );
    intersectionObserver.observe(container);
  }

  function teardownIntersectionGate() {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
    detachListeners();
  }

  function beginTrackingIfNeeded() {
    if (!viewEventSent) {
      viewSession.ensureViewId();
      viewEventSent = true;
      sendEvent(config.eventPrefix + "_view", {});
    }
    setupIntersectionGate();
    // Force an immediate pass rather than waiting for the next real scroll/resize --
    // covers "consent granted (or re-granted) after the visitor already scrolled
    // and stopped."
    attachListeners();
    recomputeReachedThresholds();
  }

  function initArticleState(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }) {
    teardownIntersectionGate();
    articleId = next.articleId;
    if (next.containerSelector) {
      container = resolveContainer(next.containerSelector);
      if (!container) {
        warn(
          config,
          'no element matched containerSelector "' +
            next.containerSelector +
            '"; scroll depth will never be measured.'
        );
      }
    }
    if ("canonicalUrl" in next) {
      currentCanonicalUrl = deriveCanonicalUrl(next.canonicalUrl);
    } else if (!currentCanonicalUrl) {
      currentCanonicalUrl = deriveCanonicalUrl(undefined);
    }
    reachedThresholds = new Set();
    viewEventSent = false;
    maximumDepthEventSent = false;
    viewSession.reset();
    activeTimer.resetForNewView();
    if (gate.isGranted()) {
      beginTrackingIfNeeded();
    }
  }

  function start() {
    if (destroyed || started) return;
    started = true;
    safely(config, () => {
      gate.bootstrapOnce();
      activeTimer.start();
      activeTimer.setConsentGranted(gate.isGranted());
      attachLifecycleListener();
      initArticleState({
        articleId: config.articleId,
        containerSelector: config.containerSelector ?? undefined,
        canonicalUrl: config.canonicalUrl,
      });
    });
  }

  function switchArticle(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }) {
    if (destroyed) return;
    safely(config, () => {
      if (!started) {
        started = true;
        gate.bootstrapOnce();
        activeTimer.start();
        activeTimer.setConsentGranted(gate.isGranted());
        attachLifecycleListener();
      } else {
        flushMaximumScrollDepth();
      }
      initArticleState(next || {});
    });
  }

  function destroy() {
    if (destroyed) return;
    safely(config, flushMaximumScrollDepth);
    destroyed = true;

    safely(config, () => teardownIntersectionGate());
    safely(config, () => detachLifecycleListener());
    safely(config, () => activeTimer.destroy());
    safely(config, () => gate.teardown());
  }

  function getDebugState() {
    return (
      safely(config, () => ({
        articleViewId: viewSession.getViewId(),
        firedThresholds: Array.from(reachedThresholds),
        maximumDepthEventSent,
        consentState: gate.getConsentState(),
        effectiveAnalyticsStorage: gate.effectiveAnalyticsStorage(),
        observingDataLayer: gate.isObserving(),
        transport: config.transport,
        eventPrefix: config.eventPrefix,
        pageType: config.pageType,
        activeTimeMsec: Math.round(activeTimer.getElapsedMsec()),
        started,
        destroyed,
      })) || null
    );
  }

  return { start, switchArticle, destroy, getDebugState };
}
